'use client';

import {PropsWithChildren, useEffect, useState} from 'react';

import {ErrorBoundary} from 'next/dist/client/components/error-boundary';
import {useSelectedLayoutSegment} from 'next/navigation';
import Script from 'next/script';

import NotFound from 'app/app/not-found';
import Banner from './core/Banner/Banner';
import {AppChainProvider} from './core/Authentication/ChainProvider';
import {initTranslations} from 'app/translations';
import {useLanguageInit} from 'app/hooks/useChangeLanguage';
import {useThemeInit} from 'app/hooks/useTheme';
import {useAuthenticationWatcher} from 'app/hooks/useAuthenticationWatcher';
import {useAuthentication} from 'app/hooks/useAuthentication';
import {useMobileClass} from 'app/hooks/useIsMobile';
import {PortraitModeWatcher} from './VerticalModeWatcher';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Notifications } from './core/Notifications/Notifications';

// Create a wrapper component that matches the expected ErrorComponent type
const ErrorWrapper = () => {
  return <NotFound />;
};

export const App = ({children}: PropsWithChildren) => {
  const [queryClient] = useState(() => new QueryClient());
  const segment = useSelectedLayoutSegment();
  const isNotFound = segment === '/_not-found';

  if (isNotFound) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary errorComponent={ErrorWrapper}>
      <Script type="text/javascript" src="/safary.js" />
      <QueryClientProvider client={queryClient}>
        <AppChainProvider>
          <AppClasses />
        <AppHooks />
        <Banner />

        <PortraitModeWatcher>{children}</PortraitModeWatcher>
        <Notifications />
        </AppChainProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

const AppHooks = () => {
  const [i18n] = useState(() => initTranslations('en'));

  //

  useLanguageInit(i18n);
  useThemeInit();
  useAuthenticationWatcher();

  return null;
};

const AppClasses = () => {
  const {connected} = useAuthentication();

  // useAnalytics();

  useMobileClass();

  useEffect(() => {
    if (!connected) return;

    document.documentElement.classList.add('connected');

    return () => {
      document.documentElement.classList.remove('connected');
    };
  }, [connected]);

  return null;
};
